import React, { useEffect, useState } from "react";
// Ícones SEMPRE na variante outline (padrão visual do menu e do
// breadcrumb) — nada de sólidos/filled misturados
import {
  HiOutlineCash,
  HiOutlineTemplate,
  HiOutlineCode,
  HiOutlineTrendingUp,
  HiOutlineUserCircle,
  HiOutlineUserRemove,
  HiOutlineBadgeCheck,
  HiOutlineTag,
  HiOutlinePuzzle,
  HiOutlineCollection,
  HiOutlineIdentification,
  HiOutlineDocumentText,
  HiOutlineServer,
  HiOutlineShieldCheck,
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineRefresh,
  HiOutlineUser,
  HiOutlineUsers,
  HiOutlineUserGroup,
  HiOutlineCog,
  HiOutlineMail,
  HiOutlineAdjustments,
  HiOutlineCurrencyDollar,
  HiOutlineLibrary,
  HiOutlineLogout,
  HiOutlineLogin,
} from "react-icons/hi";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SchoolIcon from "@mui/icons-material/SchoolOutlined";
import { FaRegIdCard } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import {
  List,
  Badge,
  Box,
  Paper,
  Popper,
  Tooltip,
  ClickAwayListener,
  Avatar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import {
  UserMenu,
  useDataProvider,
  useLogout,
  useUserMenu,
  useSidebarState,
} from "react-admin";
import BallotIcon from "@mui/icons-material/BallotOutlined";
import TextsmsOutlinedIcon from "@mui/icons-material/TextsmsOutlined";
import { IuguIcon } from "./IuguIcon";
import { determineUserReadableRole } from "../ra-components/users/UserUtils";
import { colors, radius } from "../data/tokens";
import { PUBLIC_REGISTRATION_ENABLED } from "../data/features";
import { initials } from "../util/text";
import "../index.css";

const SettingsMenuItem = React.forwardRef((props, ref) => {
  const { onClose } = useUserMenu();
  const logout = useLogout();
  const auth = localStorage.getItem("auth");
  if (auth) {
    const authJson = auth ? JSON.parse(auth) : auth;
    const id = authJson.id;
    const fullName: string = authJson.fullName || "Usuário";
    const identifier = localStorage.getItem("identifier") || "";

    // Só o logout(): o useLogout do react-admin já navega ao login e
    // limpa store + cache de consultas. A recarga completa que existia
    // aqui (window.location.href 500ms depois) era redundante e fazia
    // a tela piscar — o login aparecia suave e meio segundo depois o
    // app inteiro recarregava do zero.
    const handleLogout = () => {
      logout();
    };
    return (
      <Box sx={{ minWidth: 260 }} {...props}>
        {/* Cabeçalho: avatar + nome + identificação */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.75,
          }}
        >
          <Avatar
            sx={{
              backgroundColor: colors.bgContent,
              color: colors.primary,
              fontWeight: 600,
              width: 42,
              height: 42,
            }}
          >
            {initials(fullName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {fullName}
            </Typography>
            {identifier && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {identifier}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider />
        <List onClick={onClose} disablePadding sx={{ py: 0.5 }}>
          <ListItemButton href={"#/users/crud/" + id + "/show"} sx={{ px: 2 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <HiOutlineUser size={20} color={colors.primary} />
            </ListItemIcon>
            <ListItemText
              primary="Meu perfil"
              primaryTypographyProps={{ fontSize: "0.9rem" }}
            />
          </ListItemButton>
          <ListItemButton onClick={handleLogout} sx={{ px: 2 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <HiOutlineLogout size={20} color={colors.action} />
            </ListItemIcon>
            <ListItemText
              primary="Sair"
              primaryTypographyProps={{
                fontSize: "0.9rem",
                color: colors.action,
              }}
            />
          </ListItemButton>
        </List>
      </Box>
    );
  } else {
    return (
      <List onClick={onClose} disablePadding sx={{ minWidth: 200, py: 0.5 }} {...props}>
        <ListItemButton href={"#/login"} sx={{ px: 2 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <HiOutlineLogin size={20} color={colors.primary} />
          </ListItemIcon>
          <ListItemText
            primary="Login"
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
        </ListItemButton>
      </List>
    );
  }
});

export function AppUserMenu() {
  const auth = localStorage.getItem("auth");
  const fullName: string = auth ? JSON.parse(auth).fullName || "" : "";
  return (
    <Box>
      <UserMenu
        className="userMenuAddress"
        icon={
          fullName ? (
            <Avatar
              sx={{
                width: 34,
                height: 34,
                backgroundColor: colors.bgContent,
                color: colors.primary,
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            >
              {initials(fullName)}
            </Avatar>
          ) : (
            <HiOutlineUserCircle />
          )
        }
      >
        <SettingsMenuItem />
      </UserMenu>
    </Box>
  );
}

// ------------------------------------------------------------------
// Menu lateral orientado a dados.
// Estrutura declarada em arrays; um único componente de grupo cuida
// dos dois modos: sidebar aberta (expande inline) e recolhida
// (flyout ao passar o mouse, sem "dançar" o layout).
// ------------------------------------------------------------------

type MenuLeaf = {
  title: string;
  icon: React.ReactNode;
  path: string; // rota (sem o "#")
  isSelected: () => boolean;
  // Contador ao lado do rótulo (ex.: Contatos [65]); null/0 = sem badge
  badge?: number | null;
};

type MenuGroup = {
  id: string;
  title: string;
  icon: React.ReactNode;
  children: MenuLeaf[];
};

const iconSize = 24;

// Altura única dos itens do menu nos dois modos (aberto/recolhido)
const itemSx = { minHeight: 48 };
// Recolhida: ícone centralizado e sem a largura mínima padrão do MUI
const collapsedItemSx = { ...itemSx, justifyContent: "center", px: 2.5 };
const collapsedIconSx = { minWidth: 0 };
// Aberta: vão ícone→rótulo harmonizado (o padrão do MUI são 56px — muito)
const openIconSx = { minWidth: 40 };

function SidebarLeaf({
  item,
  inset,
  sidebarOpen,
  onNavigate,
}: {
  item: MenuLeaf;
  inset?: boolean;
  sidebarOpen: boolean;
  onNavigate: (path: string) => void;
}) {
  const hasBadge = item.badge != null && item.badge > 0;
  const badgeText =
    hasBadge && item.badge! > 99 ? "99+" : String(item.badge ?? "");
  const button = (
    <ListItemButton
      sx={
        sidebarOpen
          ? { ...itemSx, ...(inset ? { pl: 4 } : {}) }
          : collapsedItemSx
      }
      selected={item.isSelected()}
      onClick={() => onNavigate(item.path)}
    >
      <ListItemIcon sx={sidebarOpen ? openIconSx : collapsedIconSx}>
        {/* Recolhida: o contador vira bolinha sobre o ícone */}
        {hasBadge && !sidebarOpen ? (
          <Badge
            badgeContent={badgeText}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: colors.action,
                color: colors.textOnPrimary,
                fontWeight: 700,
              },
            }}
          >
            {item.icon}
          </Badge>
        ) : (
          item.icon
        )}
      </ListItemIcon>
      {sidebarOpen && <ListItemText primary={item.title} />}
      {/* Aberta: pílula de contagem ao lado do rótulo */}
      {sidebarOpen && hasBadge && (
        <Box
          component="span"
          sx={{
            marginLeft: 1,
            minWidth: 22,
            height: 22,
            paddingX: 0.75,
            borderRadius: "11px",
            backgroundColor: colors.action,
            color: colors.textOnPrimary,
            fontSize: 11.5,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {badgeText}
        </Box>
      )}
    </ListItemButton>
  );
  // Recolhida: tooltip com o nome no hover
  return sidebarOpen ? (
    button
  ) : (
    <Tooltip title={item.title} placement="right">
      {button}
    </Tooltip>
  );
}

function SidebarGroup({
  group,
  openGroup,
  setOpenGroup,
  sidebarOpen,
  onNavigate,
}: {
  group: MenuGroup;
  openGroup: string;
  setOpenGroup: (id: string) => void;
  sidebarOpen: boolean;
  onNavigate: (path: string, keepGroupId?: string) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasActiveChild = group.children.some((c) => c.isSelected());
  const isOpen = openGroup === group.id;
  // Navegar por um subitem mantém o grupo aberto
  const childNavigate = (path: string) => onNavigate(path, group.id);

  // Sidebar aberta: expansão inline com toggle (clicar de novo fecha)
  if (sidebarOpen) {
    return (
      <>
        <ListItemButton
          sx={itemSx}
          selected={hasActiveChild && !isOpen}
          onClick={() => setOpenGroup(isOpen ? "" : group.id)}
        >
          <ListItemIcon sx={openIconSx}>{group.icon}</ListItemIcon>
          <ListItemText primary={group.title} />
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {group.children.map((item) => (
              <SidebarLeaf
                key={item.path}
                item={item}
                inset
                sidebarOpen={sidebarOpen}
                onNavigate={childNavigate}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  // Sidebar recolhida: flyout ao lado no hover/clique — nada expande inline
  const closeFlyout = () => setAnchorEl(null);
  return (
    <ClickAwayListener onClickAway={closeFlyout}>
      <Box onMouseLeave={closeFlyout}>
        <ListItemButton
          sx={collapsedItemSx}
          selected={hasActiveChild}
          onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
          onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
        >
          <ListItemIcon sx={collapsedIconSx}>{group.icon}</ListItemIcon>
        </ListItemButton>
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="right-start"
          sx={{ zIndex: 1300 }}
          modifiers={[{ name: "offset", options: { offset: [0, -6] } }]}
        >
          <Paper
            className="sidebar-flyout"
            sx={{
              backgroundColor: colors.primary,
              color: colors.textOnPrimary,
              minWidth: 240,
              borderRadius: `${radius.md}px`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                fontWeight: 600,
                fontSize: "0.8rem",
                opacity: 0.8,
              }}
            >
              {group.title}
            </Box>
            <List disablePadding>
              {group.children.map((item) => (
                <ListItemButton
                  key={item.path}
                  sx={itemSx}
                  selected={item.isSelected()}
                  onClick={() => {
                    closeFlyout();
                    childNavigate(item.path);
                  }}
                >
                  <ListItemIcon sx={openIconSx}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.title} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export function AppSidebarMenu() {
  const [openGroup, setOpenGroup] = useState("members");
  const [sidebarOpen, setSidebarOpen] = useSidebarState();

  // Tablet (entre sm e lg): recolhe a sidebar automaticamente para o
  // modo mini (ícones); desktop (lg+): expande de volta. No mobile
  // (< sm) o react-admin usa overlay e não é afetado.
  const isTablet = useMediaQuery((theme: any) =>
    theme.breakpoints.between("sm", "lg")
  );
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up("lg"));
  useEffect(() => {
    if (isTablet) setSidebarOpen(false);
    else if (isDesktop) setSidebarOpen(true);
  }, [isTablet, isDesktop, setSidebarOpen]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const transactionType = queryParams.get("type");
  const currentPath = location.pathname;
  const logout = useLogout();
  // Sem recarga forçada após o logout — mesmo motivo do menu do avatar
  const handleLogout = () => {
    logout();
  };
  const isActive = (path: string) => {
    // Ativo na rota exata e também nas subrotas (ex.: /users/crud/123/show)
    return currentPath === path || currentPath.startsWith(path + "/");
  };
  // Item avulso (ex.: Dashboard) fecha grupos abertos;
  // subitem passa keepGroupId e mantém o próprio grupo aberto
  const navigate = (path: string, keepGroupId?: string) => {
    setOpenGroup(keepGroupId ?? "");
    window.location.href = "/#" + path;
  };

  const roles = localStorage.getItem("roles");
  const auth = localStorage.getItem("auth");
  const authJson = auth ? JSON.parse(auth) : null;
  const role = determineUserReadableRole(roles);
  const isAdmin = role === "admin";
  const isFinance = role === "finance";

  // Badge do item Contatos: só as mensagens NOVAS desde a última visita
  // à tela — o total exibido permanentemente virava ruído (65 fixo).
  // Novas = total atual − total registrado no navegador ao abrir a tela
  // de Contatos (o servidor não tem lido/não-lido). Recontado a cada
  // navegação (consulta barata: perPage 1, só interessa o total); abrir
  // Contatos zera o badge e registra o estado como visto. Sem marcador
  // ainda (primeiro uso no aparelho), o total inteiro conta como novo e
  // a primeira visita zera. Exclusões só reduzem o total (delta ≤ 0 →
  // badge some até chegar mensagem nova).
  const dataProvider = useDataProvider();
  const [feedbackCount, setFeedbackCount] = useState<number | null>(null);
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    dataProvider
      .getList("feedbacks/crud", {
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
        filter: {},
      })
      .then((r: any) => {
        if (!alive) return;
        const total = r.total ?? 0;
        const seenRaw = localStorage.getItem("feedbacksSeenTotal");
        // A tela de CRIAR contato não marca como visto — criar não é
        // ler a caixa de entrada (a lista/leitura, sim)
        const onContacts =
          (currentPath === "/feedbacks/crud" ||
            currentPath.startsWith("/feedbacks/crud/")) &&
          currentPath !== "/feedbacks/crud/create";
        if (onContacts) {
          localStorage.setItem("feedbacksSeenTotal", String(total));
          setFeedbackCount(0);
        } else if (seenRaw === null) {
          setFeedbackCount(total);
        } else {
          setFeedbackCount(Math.max(0, total - Number(seenRaw)));
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [dataProvider, isAdmin, currentPath]);

  if (isAdmin || isFinance || role === "secretary" || role === "comms") {
    const white = colors.textOnPrimary;

    const membersGroup: MenuGroup = {
      id: "members",
      title: "Gestão de Membros",
      icon: <HiOutlineUserGroup size={iconSize} color={white} />,
      children: [
        {
          title: "Sócios",
          icon: <HiOutlineUsers size={iconSize} color={white} />,
          path: "/users/crud",
          isSelected: () => isActive("/users/crud"),
        },
        ...(isAdmin
          ? [
              {
                title: "Membros Órfãos",
                icon: <HiOutlineUserRemove size={iconSize} color={white} />,
                path: "/orphaned-users/crud",
                isSelected: () => isActive("/orphaned-users/crud"),
              },
              {
                title: "Eleições",
                icon: <BallotIcon sx={{ color: white }} />,
                path: "/user-elections/crud",
                isSelected: () => isActive("/user-elections/crud"),
              },
            ]
          : []),
      ],
    };

    const relationshipsGroup: MenuGroup = {
      id: "relationships",
      title: "Gestão de Relacionamentos",
      icon: <HiOutlineAdjustments size={iconSize} color={white} />,
      children: [
        {
          title: "Cargos e Funções",
          icon: <FaRegIdCard size={iconSize} color={white} />,
          path: "/user-position-roles/crud",
          isSelected: () => isActive("/user-position-roles/crud"),
        },
        {
          title: "Status de Cargos e Funções",
          icon: <HiOutlineBadgeCheck size={iconSize} color={white} />,
          path: "/user-role-status/crud",
          isSelected: () => isActive("/user-role-status/crud"),
        },
        {
          title: "Categorias de Sócios",
          icon: <HiOutlineTag size={iconSize} color={white} />,
          path: "/user-categories/crud",
          isSelected: () => isActive("/user-categories/crud"),
        },
        {
          title: "Divisões Técnicas",
          icon: <HiOutlinePuzzle size={iconSize} color={white} />,
          path: "/technical-divisions/crud",
          isSelected: () => isActive("/technical-divisions/crud"),
        },
        {
          title: "Formações",
          icon: <SchoolIcon sx={{ color: white }} />,
          path: "/graduations/crud",
          isSelected: () => isActive("/graduations/crud"),
        },
        {
          title: "Instituições de Ensino",
          icon: <HiOutlineLibrary size={iconSize} color={white} />,
          path: "/educational-institutions/crud",
          isSelected: () => isActive("/educational-institutions/crud"),
        },
      ],
    };

    const transactionsGroup: MenuGroup = {
      id: "transactions",
      title: "Transações",
      icon: <HiOutlineCash size={iconSize} color={white} />,
      children: [
        {
          title: "Receitas & Despesas",
          icon: <HiOutlineTrendingUp size={iconSize} color={white} />,
          path: "/transactions/crud?type=income",
          isSelected: () => transactionType === "income",
        },
        {
          title: "Categorias",
          icon: <HiOutlineCollection size={iconSize} color={white} />,
          path: "/transaction-categories/crud",
          isSelected: () => isActive("/transaction-categories/crud"),
        },
      ],
    };

    const iuguGroup: MenuGroup = {
      id: "iugu",
      title: "Iugu (Integração)",
      icon: <IuguIcon size={iconSize} color={white} />,
      children: [
        {
          title: "Assinaturas",
          icon: <HiOutlineRefresh size={iconSize} color={white} />,
          path: "/subscriptions",
          isSelected: () => isActive("/subscriptions"),
        },
        {
          title: "Clientes",
          icon: <HiOutlineIdentification size={iconSize} color={white} />,
          path: "/iugu-customers",
          isSelected: () => isActive("/iugu-customers"),
        },
        {
          title: "Planos",
          icon: <HiOutlineViewGrid size={iconSize} color={white} />,
          path: "/iugu-planos",
          isSelected: () => isActive("/iugu-planos"),
        },
        {
          title: "Faturas",
          icon: <HiOutlineDocumentText size={iconSize} color={white} />,
          path: "/iugu-invoices",
          isSelected: () => isActive("/iugu-invoices"),
        },
        {
          title: "Itens de Cobrança",
          icon: <HiOutlineViewList size={iconSize} color={white} />,
          path: "/products/crud",
          isSelected: () => isActive("/products/crud"),
        },
      ],
    };

    const systemGroup: MenuGroup = {
      id: "system",
      title: "Sistema",
      icon: <HiOutlineCog size={iconSize} color={white} />,
      children: [
        {
          title: "Templates de E-mail",
          icon: <HiOutlineMail size={iconSize} color={white} />,
          path: "/mail-templates/crud",
          isSelected: () => isActive("/mail-templates/crud"),
        },
        {
          title: "Configurações de Sistema",
          icon: <HiOutlineServer size={iconSize} color={white} />,
          path: "/system-settings/crud",
          isSelected: () => isActive("/system-settings/crud"),
        },
        {
          title: "Regras de Usuário",
          icon: <HiOutlineShieldCheck size={iconSize} color={white} />,
          path: "/user-roles/crud",
          isSelected: () => isActive("/user-roles/crud"),
        },
      ],
    };

    const dashboardLeaf: MenuLeaf = {
      title: "Dashboard",
      icon: <HiOutlineTemplate size={iconSize} color={white} />,
      path: "/dashboard",
      isSelected: () => isActive("/dashboard"),
    };

    const groupProps = { openGroup, setOpenGroup, sidebarOpen, onNavigate: navigate };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <List
          // py 0: primeiro item encostado no topo (o padding padrão do
          // MUI deixava uma faixa sem hover visível acima do Dashboard)
          sx={{ width: "100%", flexGrow: 1, py: 0 }}
          component="nav"
          aria-labelledby="nested-list-subheader"
        >
          {(isAdmin || isFinance) && (
            <SidebarLeaf
              item={dashboardLeaf}
              sidebarOpen={sidebarOpen}
              onNavigate={navigate}
            />
          )}
          <SidebarGroup group={membersGroup} {...groupProps} />
          {isAdmin && <SidebarGroup group={relationshipsGroup} {...groupProps} />}
          {(isAdmin || isFinance) && (
            <>
              <SidebarGroup group={transactionsGroup} {...groupProps} />
              <SidebarGroup group={iuguGroup} {...groupProps} />
              {isAdmin && <SidebarGroup group={systemGroup} {...groupProps} />}
            </>
          )}
        </List>
        {/* Rodapé da sidebar: fixo embaixo (flexGrow empurra) */}
        {isAdmin && (
          <Box>
            <Divider />
            <List>
              <SidebarLeaf
                item={{
                  title: "Contatos",
                  icon: <TextsmsOutlinedIcon sx={{ color: white }} />,
                  path: "/feedbacks/crud",
                  isSelected: () => isActive("/feedbacks/crud"),
                  badge: feedbackCount,
                }}
                sidebarOpen={sidebarOpen}
                onNavigate={navigate}
              />
              <SidebarLeaf
                item={{
                  title: "Para o desenvolvedor",
                  icon: <HiOutlineCode size={iconSize} color={white} />,
                  path: "/api",
                  isSelected: () => isActive("/api"),
                }}
                sidebarOpen={sidebarOpen}
                onNavigate={navigate}
              />
            </List>
          </Box>
        )}
      </Box>
    );
  } else if (role === "user") {
    const white = colors.textOnPrimary;
    const userItems: MenuLeaf[] = [
      {
        title: "Meu Cadastro",
        icon: <HiOutlineUser size={iconSize} color={white} />,
        path: "/users/crud/" + (authJson ? authJson.id : "") + "/show",
        isSelected: () => isActive("/users-crud"),
      },
      {
        title: "Minhas Cobranças",
        icon: <HiOutlineCurrencyDollar size={iconSize} color={white} />,
        path: "/my-invoices",
        isSelected: () => isActive("/my-invoices"),
      },
      {
        title: "Minhas Assinaturas",
        icon: <HiOutlineRefresh size={iconSize} color={white} />,
        path: "/my-subscriptions",
        isSelected: () => isActive("/my-subscriptions"),
      },
      {
        title: "Contato",
        icon: <TextsmsOutlinedIcon sx={{ color: white }} />,
        path: "/feedbacks/crud/create",
        isSelected: () => isActive("/feedbacks/crud/create"),
      },
    ];
    return (
      <List>
        {userItems.map((item) => (
          <SidebarLeaf
            key={item.path}
            item={item}
            sidebarOpen={sidebarOpen}
            onNavigate={navigate}
          />
        ))}
        <ListItemButton selected={isActive("/logout")} onClick={handleLogout}>
          <ListItemIcon sx={openIconSx}>
            <HiOutlineLogout size={iconSize} color={colors.textOnPrimary} />
          </ListItemIcon>
          {sidebarOpen && <ListItemText primary="Sair" />}
        </ListItemButton>
      </List>
    );
  } else {
    const white = colors.textOnPrimary;
    return (
      <List>
        <SidebarLeaf
          item={{
            title: "Login",
            icon: <HiOutlineLogin size={iconSize} color={white} />,
            path: "/login",
            isSelected: () => isActive("/login"),
          }}
          sidebarOpen={sidebarOpen}
          onNavigate={navigate}
        />
        {/* Some enquanto o cadastro estiver desativado (data/features.ts) */}
        {PUBLIC_REGISTRATION_ENABLED && (
          <SidebarLeaf
            item={{
              title: "Cadastro",
              icon: <HiOutlineUser size={iconSize} color={white} />,
              path: "/register",
              isSelected: () => isActive("/register"),
            }}
            sidebarOpen={sidebarOpen}
            onNavigate={navigate}
          />
        )}
      </List>
    );
  }
}
